// Anonymous view of a publicly shared dashboard, rendered at /share/:shareId
// — reached by main.tsx's pre-auth pathname branch, entirely outside the
// authenticated router/nav chrome. A single fetch (no poll): the share's
// view count increments once per GET, so polling here would inflate it into
// a "poll count" rather than a "view count".
import { useClient } from '@cortex/react-query';
import type { PublicDashboardView } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';

import { Spinner } from '../components/common/Spinner';
import { DashboardLayout } from '../components/dashboards/DashboardLayout';

import { PublicWidgetFrame } from './PublicWidgetFrame';

export function PublicShareView({ shareId }: { shareId: string }) {
  const client = useClient();

  const view = useQuery({
    queryKey: ['public-share', shareId],
    queryFn: () =>
      client.http
        .get(`v1/dashboard-shares/${shareId}`, { cache: 'no-store' })
        .json<PublicDashboardView>(),
    staleTime: Infinity,
    retry: false,
  });

  if (view.isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    );
  }
  if (view.error || !view.data) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-muted)',
        }}
      >
        This share link is invalid or has been revoked.
      </div>
    );
  }
  const d = view.data;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        minHeight: 0,
        padding: 'var(--sp-3)',
      }}
    >
      <DashboardLayout
        kind={d.kind}
        gridCols={d.gridCols}
        gridRows={d.gridRows}
        primaryWidgetId={d.primaryWidgetId}
        widgets={d.widgets}
        renderWidget={(w) => <PublicWidgetFrame shareId={shareId} widget={w} />}
      />
    </div>
  );
}
